import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Permission } from 'src/app/constants/permission.constant';
import { IProduct } from 'src/app/models/products/product';
import { AuthService } from 'src/app/services/auth.service';
import { ProductsService } from 'src/app/services/products.service';
import { AlertService } from 'src/app/services/alert.service';
import { MasterSuiteDialogComponent } from 'src/app/components/home/mastersuites/master-suite-dialog/master-suite-dialog.component';
import { WarningModalComponent } from 'src/app/components/shared/warning-modal/warning-modal.component';

@Component({
  selector: 'app-enhancement-variants-change-action',
  templateUrl: './change-enhancement-variant-action.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ChangeEnhancementVariantActionComponent {
  @Input() product!: IProduct;
  @Input() enhancementVariantIdSelected: string | null = '';
  @Input() displayMode: 'icon' | 'menu-item' = 'icon';
  @Output() enhancementVariantChanged = new EventEmitter<string | null>();

  Permission = Permission;

  constructor(
    public authService: AuthService,
    private productsService: ProductsService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  onChangeEnhancementVariant(): void {
    const dialogRef = this.openEnhancementVariantDialog();
    dialogRef.afterClosed().subscribe((res) => {
      this.handleDialogResult(res);
    });
  }

  private openEnhancementVariantDialog() {
    return this.dialog.open(MasterSuiteDialogComponent, {
      data: {
        masterSuiteId: this.product.masterSuiteId,
        masterSuiteName: this.product.masterSuiteName,
        productIds: [this.product.id],
        masterSuiteEnhancementVariantId: this.enhancementVariantIdSelected,
      },
    });
  }

  private hasNoEnhancementVariants(res: any): boolean {
    return res?.enhancementVariants?.length === 0;
  }

  private hasSelectedEnhancementVariant(res: any): boolean {
    return res && !res?.enhancementVariants;
  }

  private isNewEnhancementVariantSelected(res: any): boolean {
    return (
      res?.enhancementVariantSelected?.id !== this.product.masterSuiteEnhancementVariantId &&
      res != undefined &&
      res?.enhancementVariantSelected
    );
  }

  private handleDialogResult(res: any): void {
    if (this.hasNoEnhancementVariants(res)) {
      this.openNoEnhancementVariantsWarning();
    }
    if (this.hasSelectedEnhancementVariant(res)) {
      this.applyEnhancementVariant(res.enhancementVariantSelected.id);
    }
    if (this.isNewEnhancementVariantSelected(res)) {
      this.enhancementVariantChanged.emit(res.enhancementVariantSelected.id);
    }
  }

  private openNoEnhancementVariantsWarning(): void {
    const dialogRef = this.dialog.open(WarningModalComponent, {
      data: {
        header: 'Warning',
        content: 'Master suite has no enhancement variants, would you like to create one?',
        titleButtonOk: 'Yes',
        showBtnNo: true,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.navigateToCreateEnhancementVariant();
      }
    });
  }

  private navigateToCreateEnhancementVariant(): void {
    this.router.navigate(
      [`mastersuites/${this.product.masterSuiteId}/update/enhancement-variant`],
      { queryParams: { applyAll: true } },
    );
  }

  private applyEnhancementVariant(enhancementVariantId: string): void {
    this.productsService
      .applyEnhancementVariantOnProducts({
        productIds: [this.product.id || ''],
        enhancementVariantId,
        version: 0,
      })
      .subscribe(() => {
        this.alertService.successToast('Selected enhancement variant default successfully!');
      });
  }
}
